import React from 'react';
import {
  Form,
  TextFieldControl,
  Information,
  Spinner,
  ModalView,
  Stack,
  withTranslation,
} from '@piwikpro/ui-components';
import { withInject } from '@piwikpro/platform';
import { Apps } from '@piwikpro/apps-crate';
import { OAuthService } from '@piwikpro/oauth-crate';
import { TFunction } from 'react-i18next';
import { FormValidator } from '@piwikpro/form-crate';
import { FormikProps, FormikValues } from 'formik';

interface InjectedProps {
  apps: Apps
  oauth: OAuthService
  validation: FormValidator
}

export interface ICreateCredentialsProps extends InjectedProps {
  abort: () => void
  t: TFunction
}

export interface ICreateCredentialsState {
  cnilInfo: {
    isLoading: boolean
    enabled: boolean
  }
}

class CreateCredentials extends React.Component<ICreateCredentialsProps, ICreateCredentialsState> {
  state = {
    cnilInfo: {
      isLoading: true,
      enabled: true,
    },
  };

  constructor(props: ICreateCredentialsProps) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  async componentDidMount() {
    const { apps } = this.props;

    try {
      const { cnil: enabled } = await apps.getInfoAboutCnilSetting();

      this.setState({
        cnilInfo: {
          isLoading: false,
          enabled,
        },
      });
    } catch {
      this.setState({
        cnilInfo: {
          isLoading: false,
          enabled: true,
        },
      });
    }
  }

  async handleSubmit(values: {
    clientName: string
  }, {
    setSubmitting,
    setErrors,
  }: FormikProps<FormikValues>) {
    const { oauth } = this.props;

    try {
      await oauth.createClient(values.clientName);

      setSubmitting(false);
    } catch (err: any) {
      setErrors(err.errors);
      setSubmitting(false);
    }
  }

  render() {
    const { abort, validation, t } = this.props;
    const { cnilInfo: { isLoading, enabled } } = this.state;

    return (
      <Form
        id="generate-credentials-form"
        initialValues={{
          clientName: '',
        }}
        validationSchema={validation.createValidationSchema(validators => ({
          clientName: validators.userPanel.clientName,
        }))}
        onSubmit={this.handleSubmit}
      >
        <ModalView
          title={t('credentials.generate.create.title')}
          actions={(
            <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
              <Form.Cancel onClick={abort} />
              <Form.Submit />
            </Stack>
          )}
        >
          {isLoading
            ? <Spinner />
            : (
              <Stack vertical>
                <Stack.Item fill>
                  <TextFieldControl
                    label={t('credentials.generate.field.name')}
                    name="clientName"
                    autoFocus
                    required
                  />
                </Stack.Item>
                {enabled && (
                  <Stack.Item>
                    <Information type="info">{t('credentials.generate.cnil.info') as string}</Information>
                  </Stack.Item>
                )}
              </Stack>
            )}
        </ModalView>
      </Form>
    );
  }
}

export default withInject<InjectedProps>({
  validation: 'FormCrate.validation',
  oauth: 'OAuthCrate.oauth',
  apps: 'AppsCrate.apps',
})(withTranslation(['profile', 'platform'])(CreateCredentials));
